// frontend/src/utils/axiosConfig.js - improved error handling
import axios from 'axios';
import { addRefreshTimestamp, isProductUrl } from './refreshUtils';

// Set the baseURL dynamically based on environment
const baseURL = process.env.NODE_ENV === 'production'
  ? '/api/v1'  // For production - use relative URL with Nginx proxy
  : '/api/v1';  // Local development - uses proxy from package.json

// Create separate instances for public and authenticated requests
const publicInstance = axios.create({
  baseURL,
  withCredentials: false // Don't send cookies for public requests
});

const instance = axios.create({
  baseURL,
  withCredentials: true // Send cookies for authenticated requests
});

// Add response interceptors to both instances
const responseInterceptor = (response) => {
  // Image processing logic remains the same...
  return response;
};

const errorInterceptor = (error) => {
  console.log('Response error:', error.message);
  
  // Only redirect for authenticated routes where the token was once valid but is now expired
  // For regular public pages, just log the error and continue
  if (error.response?.status === 401 && window.location.pathname.includes('/my')) {
    if (localStorage.getItem('deliveryStaffInfo')) {
      localStorage.removeItem('deliveryStaffInfo');
      window.location.href = '/delivery/login';
    } else if (localStorage.getItem('adminInfo')) {
      localStorage.removeItem('adminInfo');
      window.location.href = '/admin/login';
    }
  }
  
  return Promise.reject(error);
};

// Apply interceptors to both instances
publicInstance.interceptors.response.use(responseInterceptor, errorInterceptor);
instance.interceptors.response.use(responseInterceptor, errorInterceptor);

// Set common headers for both instances
publicInstance.defaults.headers.common['Content-Type'] = 'application/json';
instance.defaults.headers.common['Content-Type'] = 'application/json';

// Cache-busting interceptor for public requests
const publicRequestInterceptor = (config) => {
  console.log('=== PUBLIC REQUEST INTERCEPTOR ===');
  console.log('URL:', config.url);
  console.log('Method:', config.method);
  console.log('Headers:', config.headers);
  console.log('WithCredentials:', config.withCredentials);
  
  // Add aggressive cache-busting for product-related requests
  if (config.url && isProductUrl(config.url)) {
    // Use the refresh utility for consistent cache-busting
    config.url = addRefreshTimestamp(config.url);
    
    // Add comprehensive no-cache headers
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate, max-age=0, private';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    config.headers['If-Modified-Since'] = '0';
    config.headers['If-None-Match'] = '*';
    config.headers['Last-Modified'] = new Date().toUTCString();
    
    // Log for debugging
    console.log(`Public cache-busting request: ${config.url}`);
  }
  
  console.log('Final config:', config);
  return config;
};

// Authentication and cache-busting interceptor for authenticated requests
const authRequestInterceptor = (config) => {
  console.log('=== AUTH REQUEST INTERCEPTOR ===');
  console.log('URL:', config.url);
  console.log('Method:', config.method);
  console.log('Headers:', config.headers);
  console.log('WithCredentials:', config.withCredentials);
  
  const deliveryStaffInfo = localStorage.getItem('deliveryStaffInfo');
  const adminInfo = localStorage.getItem('adminInfo');
  
  console.log('Auth info:', {
    deliveryStaffInfo: deliveryStaffInfo ? 'Present' : 'Not Present',
    adminInfo: adminInfo ? 'Present' : 'Not Present'
  });
  
  // Add aggressive cache-busting for product-related requests
  if (config.url && isProductUrl(config.url)) {
    // Use the refresh utility for consistent cache-busting
    config.url = addRefreshTimestamp(config.url);
    
    // Add comprehensive no-cache headers
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate, max-age=0, private';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    config.headers['If-Modified-Since'] = '0';
    config.headers['If-None-Match'] = '*';
    config.headers['Last-Modified'] = new Date().toUTCString();
    
    // Log for debugging
    console.log(`Auth cache-busting request: ${config.url}`);
  }
  
  if (deliveryStaffInfo) {
    try {
      const { token } = JSON.parse(deliveryStaffInfo);
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Added delivery staff authorization');
    } catch (e) {
      // Handle parsing error gracefully
      console.error('Error parsing deliveryStaffInfo', e);
      localStorage.removeItem('deliveryStaffInfo');
    }
  } else if (adminInfo) {
    try {
      const { token } = JSON.parse(adminInfo);
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Added admin authorization');
    } catch (e) {
      // Handle parsing error gracefully
      console.error('Error parsing adminInfo', e);
      localStorage.removeItem('adminInfo');
    }
  }
  
  console.log('Final auth config:', config);
  return config;
};

// Apply request interceptors
publicInstance.interceptors.request.use(publicRequestInterceptor, (error) => {
  console.error('Public request error:', error);
  return Promise.reject(error);
});

instance.interceptors.request.use(authRequestInterceptor, (error) => {
  console.error('Auth request error:', error);
  return Promise.reject(error);
});

// Export both instances
export default instance; // For authenticated requests
export { publicInstance }; // For public requests